import React, { useEffect, useRef, useState } from 'react';
import { TRACK_REACTIONS, trackOccurrenceKey } from '../../utils/trackReactions';
import useReactionStore from '../../stores/reactionStore';
import usePlayerStore from '../../stores/playerStore';

export const REACTION_BURST_MS = 1000;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function TrackReactionBar({ track, size = 'md' }) {
  const occurrenceKey = trackOccurrenceKey(track);
  const selected = useReactionStore((state) => (
    occurrenceKey ? state.byOccurrence[occurrenceKey]?.type || null : null
  ));
  const currentSong = usePlayerStore((state) => state.currentSong);
  const currentKey = trackOccurrenceKey(currentSong);
  const live = Boolean(occurrenceKey && currentKey && occurrenceKey === currentKey);
  const compact = size === 'sm';
  const [editing, setEditing] = useState(false);
  const [burstId, setBurstId] = useState(null);
  const burstTimer = useRef(null);
  const locked = Boolean(selected) && !editing;
  const mode = selected ? (editing ? 'editing' : 'locked') : 'none';

  useEffect(() => () => {
    if (burstTimer.current) window.clearTimeout(burstTimer.current);
  }, []);

  if (!track || !occurrenceKey) return null;

  const startBurst = (reactionId) => {
    if (burstTimer.current) window.clearTimeout(burstTimer.current);
    if (prefersReducedMotion()) {
      setBurstId(null);
      return;
    }
    setBurstId(reactionId);
    burstTimer.current = window.setTimeout(() => {
      setBurstId(null);
    }, REACTION_BURST_MS);
  };

  const commitReaction = (reactionId) => {
    if (locked) return;
    if (selected === reactionId) {
      setEditing(false);
      return;
    }
    startBurst(reactionId);
    useReactionStore.getState().setReaction(track, reactionId, { live });
    setEditing(false);
  };

  return (
    <div
      role="group"
      aria-label="Track reaction"
      data-reaction-mode={mode}
      className={`flex items-center ${compact ? 'gap-0.5' : 'gap-1'}`}
    >
      {TRACK_REACTIONS.map((reaction) => {
        const isSelected = selected === reaction.id;
        const isBursting = burstId === reaction.id;
        const isDisabled = locked && !isSelected;
        return (
          <button
            key={reaction.id}
            type="button"
            aria-label={reaction.label}
            aria-pressed={isSelected}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            data-reaction={reaction.id}
            data-reaction-burst={isBursting ? reaction.id : undefined}
            onClick={(event) => {
              event.stopPropagation();
              commitReaction(reaction.id);
            }}
            className={`reaction-btn reaction-btn--${reaction.id} ${
              compact ? 'min-h-8 min-w-8 text-sm' : 'min-h-10 min-w-10 text-base'
            } ${isSelected ? 'reaction-btn--selected' : ''} ${
              isBursting ? 'reaction-btn--burst' : ''
            } ${isDisabled ? 'reaction-btn--locked-idle' : ''}`}
          >
            <span className="reaction-btn__emoji" aria-hidden="true">{reaction.emoji}</span>
            {isBursting && reaction.id === 'love' ? (
              <>
                <span className="reaction-sparkle reaction-sparkle--1" aria-hidden="true">✦</span>
                <span className="reaction-sparkle reaction-sparkle--2" aria-hidden="true">✧</span>
                <span className="reaction-sparkle reaction-sparkle--3" aria-hidden="true">✦</span>
                <span className="reaction-sparkle reaction-sparkle--4" aria-hidden="true">✧</span>
              </>
            ) : null}
            {isBursting && reaction.id === 'down' ? (
              <span className="reaction-sad" aria-hidden="true">🥺</span>
            ) : null}
          </button>
        );
      })}
      {selected ? (
        <button
          type="button"
          className={`reaction-edit-btn ${compact ? 'min-h-8 px-1.5 text-[10px]' : 'min-h-8 px-2 text-xs'}`}
          onClick={(event) => {
            event.stopPropagation();
            if (burstTimer.current) window.clearTimeout(burstTimer.current);
            setBurstId(null);
            setEditing((current) => !current);
          }}
        >
          {editing ? 'Cancel' : 'Change'}
        </button>
      ) : null}
    </div>
  );
}
